<script setup>

// Imports
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'

// Props
const props = defineProps({
  block: {
    type: Object,
    default: () => ({})
  }
})

/*
  A stack of stat cards: the active one in front, the next one peeking out below it.
  Every HOLD ms the front card flows up and fades out, the next one moves to the front and a
  queued one fades in behind it — an infinite loop with no scroll trigger and no pause on hover.
*/
const HOLD = 3000
const MOVE = 600

const cards = computed(() => (Array.isArray(props.block?.cards) ? props.block.cards : []))

const activeIndex = ref(0)
// The card that just left the front, while it animates out
const exitingIndex = ref(null)
// A card that has finished leaving and snaps (without a transition) back into the queue
const resettingIndex = ref(null)

let timer = null
let exitTimer = null
let reducedMotion = null

const cardState = (index) => {
  if (index === exitingIndex.value) return 'is-exiting'
  if (index === resettingIndex.value) return 'is-queued is-resetting'

  const count = cards.value.length
  const position = (index - activeIndex.value + count) % count

  if (position === 0) return 'is-front'
  if (position === 1) return 'is-next'
  return 'is-queued'
}

const step = () => {
  const count = cards.value.length
  if (count < 2) return

  exitingIndex.value = activeIndex.value
  activeIndex.value = (activeIndex.value + 1) % count

  clearTimeout(exitTimer)
  exitTimer = setTimeout(() => {
    resettingIndex.value = exitingIndex.value
    exitingIndex.value = null
    // Two frames so the snap is painted before the card may transition again
    requestAnimationFrame(() => requestAnimationFrame(() => {
      resettingIndex.value = null
    }))
  }, MOVE)
}

const start = () => {
  if (timer || cards.value.length < 2 || reducedMotion?.matches || document.hidden) return
  timer = setInterval(step, HOLD + MOVE)
}

const stop = () => {
  clearInterval(timer)
  timer = null
}

// Reduced motion: the stack freezes on the first card
const sync = () => {
  stop()
  start()
}

onMounted(() => {
  reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
  reducedMotion.addEventListener('change', sync)
  // Background tabs throttle timers; restart cleanly instead of catching up
  document.addEventListener('visibilitychange', sync)
  start()
})

onBeforeUnmount(() => {
  stop()
  clearTimeout(exitTimer)
  reducedMotion?.removeEventListener('change', sync)
  document.removeEventListener('visibilitychange', sync)
})

</script>

<!-- -------------------------------------------------------------------------------------------------
    Template
------------------------------------------------------------------------------------------------- -->

<template>
<section class="stats-marquee section-mb">
  <div class="container">

    <ul v-if="cards.length"
      class="stats-marquee__grid">
      <li v-for="(card, index) in cards"
        :key="card.id"
        class="stats-marquee__card"
        :class="cardState(index)">
        <p class="stats-marquee__card-title">
          {{ card.title }}
        </p>
        <p class="stats-marquee__card-description">
          {{ card.description }}
        </p>
      </li>
    </ul>
  </div>
</section>
</template>

<!-- -------------------------------------------------------------------------------------------------
    Styling
------------------------------------------------------------------------------------------------- -->

<style scoped>

/*
  All cards share one grid cell, so the stack is as tall as the tallest card; the padding leaves
  room for the next card peeking out below the front one.
*/
.stats-marquee__grid {
  --peek: 67px;
  --move: .6s;
  --ease: cubic-bezier(.42, 0, .58, 1);

  display: grid;
  margin: 0;
  padding: 0 0 var(--peek);
  list-style: none;

  @media (min-width: 1024px) {
    --peek: 90px;
  }
}

.stats-marquee__card {
  grid-area: 1 / 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 20px 24px;
  border-radius: 16px;
  background-color: #edebe6;
  color: #292016;
  transform-origin: center bottom;
  transition:
    transform var(--move) var(--ease),
    opacity var(--move) var(--ease),
    background-color var(--move) var(--ease),
    color var(--move) var(--ease);

  @media (min-width: 1024px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: 48px;
    padding: 20px 56px;
  }

  @media (min-width: 1440px) {
    padding: 20px 80px;
  }
}

/* Stack positions */
.stats-marquee__card.is-front {
  z-index: 3;
}

.stats-marquee__card.is-next {
  z-index: 2;
  background-color: #f7f5f0;
  color: #a8a39b;
  transform: translateY(var(--peek)) scale(.9);
}

.stats-marquee__card.is-queued {
  z-index: 1;
  background-color: #f7f5f0;
  color: #a8a39b;
  opacity: 0;
  transform: translateY(calc(var(--peek) * 2)) scale(.8);
}

.stats-marquee__card.is-exiting {
  z-index: 4;
  opacity: 0;
  transform: translateY(-40px);
}

.stats-marquee__card.is-resetting {
  transition: none;
}

.stats-marquee__card-title,
.stats-marquee__card-description {
  margin: 0;
  color: inherit;
}

.stats-marquee__card-title {
  flex: none;
  font-family: var(--font-family-heading);
  font-size: 50px;
  font-weight: 500;
  line-height: 1.2;
  letter-spacing: -.02em;

  @media (min-width: 1024px) {
    font-size: 96px;
    font-weight: 400;
  }

  @media (min-width: 1440px) {
    font-size: 130px;
  }
}

.stats-marquee__card-description {
  max-width: 260px;
  font-family: var(--font-family-primary);
  font-size: 16px;
  font-weight: 400;
  line-height: 1.4;
  letter-spacing: -.02em;

  @media (min-width: 1024px) {
    max-width: 460px;
    font-size: 24px;
    line-height: 1.2;
  }

  @media (min-width: 1440px) {
    max-width: 595px;
    font-size: 32px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .stats-marquee__card {
    transition: none;
  }
}

</style>
